import {
  AnalyticsWorkflowError,
  type AnalyticsFailureKind,
} from "../../../../../lib/analyticsWorkflow";

/**
 * Banner copy per failure kind, written here rather than lifted from the error.
 *
 * What an auditor needs told — what happened to this cell and what to do about
 * it — is not what the route or the parse that failed had to say, so the copy
 * is authored against the kind instead of rendering
 * `AnalyticsWorkflowError.message`. Keyed by the kind union so a new kind fails
 * to compile here instead of silently falling through to generic copy.
 */
const ANALYTICS_ERROR_MESSAGES: Record<AnalyticsFailureKind, string> = {
  workflow_deleted:
    "The workflow behind this verdict has been deleted, so the result can no longer be loaded.",
  not_found: "This analytics result could not be loaded.",
  permission_denied:
    "You do not have permission to view this analytics result.",
  conflict:
    "This analytics result changed while it was loading. Retry to load the current one.",
  rejected: "This request was refused, so the analytics result did not load.",
  session_expired:
    "Your session ended before this result loaded. Sign in again to continue.",
  upstream_unavailable:
    "The analytics service is unavailable right now, so this result could not be loaded.",
  unreachable:
    "This result could not be loaded because the request never reached the server.",
  unreadable:
    "This analytics result could not be read. Refresh the page to see the current state.",
  unknown: "Something went wrong loading this analytics result.",
};

/**
 * The sentence to show for a failed verdict read, on whichever surface read it.
 *
 * The sheet and the fold-out's review tab read the same verdict through the
 * same hook, so one failure owes them one sentence. A module of its own rather
 * than an export from either host because `react-refresh/only-export-components`
 * refuses a component file that also exports a function, and the repo lints at
 * `--max-warnings 0`.
 */
export function analyticsErrorMessage(error: Error): string {
  return error instanceof AnalyticsWorkflowError
    ? ANALYTICS_ERROR_MESSAGES[error.kind]
    : ANALYTICS_ERROR_MESSAGES.unknown;
}
